import { createContext, useCallback, useContext, useEffect } from "react";
import { AuthApi } from "../api";
import {
  UpdateUserInput,
  UserCreateParams,
  UserLoginParams,
  userPermissions,
  UserUpdateParams,
} from "../../../types/user";
import { useLocalStorageState } from "../hooks";
import { logPile } from "../logPile";
import {
  ChallengeChangePasswordInput,
  ChallengeCreateInput,
  ChallengeLoginInput,
} from "../../../types";

export type Loadable<DataType> =
  | {
      data?: DataType | undefined;
      loading: false;
      error?: string;
    }
  | {
      data?: DataType | undefined;
      loading: true;
      error?: string;
    }
  | {
      data: DataType;
      loading: false;
      error?: string;
    }
  | {
      data?: DataType;
      loading: false;
      error: string;
    };

export const setAuthToken = (token: string) => {
  // set token in local storage
  window.localStorage.setItem("auth", token);
};

export const getAuthToken = () => {
  // get token from local storage
  return window.localStorage.getItem("auth");
};

export const removeAuthToken = () => {
  // remove token from local storage
  window.localStorage.removeItem("auth");
};
const authContext = createContext<{
  userPermissions?: Loadable<userPermissions>;
  login?: (
    input: UserLoginParams,
  ) => Promise<{ success: boolean; error?: string }>;
  signup?: (
    input: UserCreateParams,
  ) => Promise<{ success: boolean; error?: string }>;
  createChallenge?: (
    input: ChallengeCreateInput,
  ) => Promise<{ success: boolean; error?: string }>;
  loginWithChallenge?: (
    input: ChallengeLoginInput,
  ) => Promise<{ success: boolean; error?: string }>;
  validateChangePassword?: (
    input: Pick<ChallengeChangePasswordInput, "code" | "email">,
  ) => Promise<{ success: boolean; error?: string }>;
  changePassword?: (
    input: ChallengeChangePasswordInput,
  ) => Promise<{ success: boolean; error?: string }>;
  updateUser?: (
    input: UserUpdateParams,
  ) => Promise<{ success: boolean; error?: string }>;
  logout?: () => Promise<void>;
}>({});

export const AuthProvider = ({ children }: { children: JSX.Element }) => {
  const [userPermissions, setUserPermissions] = useLocalStorageState<
    Loadable<userPermissions>
  >("userPermissions", { loading: false });
  // const [user, setUser] = useLocalStorageState<UserData>();
  // useEffect(() => {
  //   if (user) {
  //     setUserPermissions({
  //       loading: false,
  //       data: { loggedIn: true, user },
  //     });
  //   } else {
  //     setUserPermissions({
  //       loading: false,
  //       data: { loggedIn: false, user: undefined },
  //       error: undefined,
  //     });
  //   }
  // }, [user, setUserPermissions]);

  // Keep log-pile's notion of "who" in step with the session.
  useEffect(() => {
    const user = userPermissions?.data?.user as
      | { id?: string; username?: string }
      | undefined;
    logPile.setUser(
      userPermissions?.data?.loggedIn && user?.id
        ? { id: user.id, username: user.username }
        : undefined,
    );
  }, [userPermissions]);

  useEffect(() => {
    const attemptRefresh = async () => {
      const token = getAuthToken();
      // TODO: handle token expired error gracefully
      // ex: REFRESH TOKEN IF FAILED DUE EXPIRED

      if (token) {
        const { success, data: user } = await AuthApi.getUser(token);
        if (success === false) {
          setUserPermissions({
            loading: false,
            data: { loggedIn: false, user: undefined },
          });
        } else {
          setUserPermissions({
            loading: false,
            data: { loggedIn: success, user },
          });
        }
      }
    };
    attemptRefresh();
  }, []);
  const login = useCallback(
    async (input: UserLoginParams) => {
      try {
        setUserPermissions({
          loading: true,
        });
        //call login api
        const data = await AuthApi.login(input);
        if (data.success === false) {
          throw Error(data.message);
        }

        setAuthToken(data.token);
        // get user data from endpoint
        const { data: user } = await AuthApi.getUser(data.token);

        logPile.setUser({ id: user.id });
        logPile.captureEvent("logged in");

        setUserPermissions({
          loading: false,
          data: { loggedIn: true, user },
        });
        return { success: true, user };
      } catch (e: any) {
        setUserPermissions({
          loading: false,
          error: e.message,
        });
        return { success: false, error: e.message } as const;
      }
    },
    [setUserPermissions],
  );
  const signup = useCallback(
    async (input: UserCreateParams) => {
      try {
        //call login api
        const data = await AuthApi.create(input);
        console.log(data);
        if (data.success) {
          setAuthToken(data.token);
          // get user data from endpoint
          const { data: user } = await AuthApi.getUser(data.token);

          logPile.setUser({ id: user.id });
          logPile.captureEvent("signed up");
          // setUser(user);
          setUserPermissions({
            loading: false,
            data: { loggedIn: true, user },
          });
          return { success: true, user };
        } else {
          return data;
        }
      } catch (e: any) {
        return { success: false, error: e.message } as const;
      }
    },
    [setUserPermissions],
  );

  const createChallenge = useCallback(async (input: ChallengeCreateInput) => {
    try {
      const data = await AuthApi.createAuthChallenge(input);
      if (data.success === false) {
        throw Error(data.message);
      }
      return { success: true };
    } catch (e: any) {
      return { success: false, error: e.message } as const;
    }
  }, []);

  const loginWithChallenge = useCallback(
    async (input: ChallengeLoginInput) => {
      try {
        setUserPermissions({
          loading: true,
        });
        const data = await AuthApi.loginWithChallenge(input);
        if (data.success === false) {
          throw Error(data.message);
        }
        setAuthToken(data.token);
        // get user data from endpoint
        const { data: user } = await AuthApi.getUser(data.token);

        logPile.setUser({ id: user.id });
        logPile.captureEvent("logged in with challenge");

        setUserPermissions({
          loading: false,
          data: { loggedIn: true, user },
        });
        return { success: true, user };
      } catch (e: any) {
        setUserPermissions({
          loading: false,
          error: e.message,
        });
        return { success: false, error: e.message } as const;
      }
    },
    [setUserPermissions],
  );

  const validateChangePassword = useCallback(
    async (input: Pick<ChallengeChangePasswordInput, "code" | "email">) => {
      try {
        const data = await AuthApi.validateChallenge(input);
        if (data.success === false) {
          throw Error(data.message);
        }
        return { ...data, success: true };
      } catch (e: any) {
        return { success: false, error: e.message } as const;
      }
    },
    [],
  );
  const changePassword = useCallback(
    async (input: ChallengeChangePasswordInput) => {
      try {
        if (!input.password) {
          throw Error("Please input password");
        }
        const data = await AuthApi.changePassword(input);
        if (data.success === false) {
          throw Error(data.message);
        }
        return { success: true };
      } catch (e: any) {
        return { success: false, error: e.message } as const;
      }
    },
    [],
  );

  const updateUser = useCallback(async (input: UserUpdateParams) => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw Error("There is an issue verifying your login");
      }
      if (!input.name) {
        throw Error("Please input name");
      }
      const data = await AuthApi.updateUser(input);

      const { data: user } = await AuthApi.getUser(token);
      setUserPermissions({
        loading: false,
        data: { loggedIn: true, user },
      });

      return data;
    } catch (e: any) {
      return { success: false, error: e.message } as const;
    }
  }, []);
  const logout = useCallback(async () => {
    logPile.captureEvent("logged out");
    logPile.setUser(undefined);
    logPile.reset();
    removeAuthToken();
    setUserPermissions({
      loading: false,
      data: { loggedIn: false, user: undefined },
      error: undefined,
    });
  }, [setUserPermissions]);

  return (
    <authContext.Provider
      value={{
        userPermissions,
        login,
        signup,
        loginWithChallenge,
        createChallenge,
        validateChangePassword,
        changePassword,
        updateUser,
        logout,
      }}
    >
      <>{children}</>
    </authContext.Provider>
  );
};
export const usePermissions = () => {
  const data = useContext(authContext);
  return data.userPermissions;
};

export const useAuthTools = () => {
  const data = useContext(authContext);
  return data.login &&
    data.signup &&
    data.loginWithChallenge &&
    data.validateChangePassword &&
    data.changePassword &&
    data.createChallenge &&
    data.updateUser
    ? {
        login: data.login,
        signup: data.signup,
        logout: data.logout,
        createChallenge: data.createChallenge,
        loginWithChallenge: data.loginWithChallenge,
        validateChangePassword: data.validateChangePassword,
        changePassword: data.changePassword,
        updateUser: data.updateUser,
      }
    : {};
};
